/**
 * Authentication hook for React state management.
 * 
 * This hook provides authentication state and methods using React Context
 * and useReducer for centralized auth management across the application.
 */

import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import {
  loginUser,
  registerUser,
  logoutUser,
  getCurrentUser,
  getStoredUser,
  isAuthenticated,
} from '../services/auth';

// Authentication context
const AuthContext = createContext();

// Auth state initial values
const initialState = {
  user: null,
  token: null,
  isLoading: true,
  isAuthenticated: false,
  error: null,
};

// Auth reducer actions
const authReducer = (state, action) => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    
    case 'AUTH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        error: null,
      };
    
    case 'AUTH_FAILURE':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        token: null,
        error: action.payload,
      };
    
    case 'AUTH_LOGOUT':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        token: null,
        error: null,
      };
    
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    
    default:
      return state;
  }
};

/**
 * Auth Context Provider Component
 */
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing authentication on mount
  useEffect(() => {
    const initializeAuth = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        if (isAuthenticated()) {
          // Try to get current user info to validate token
          const user = await getCurrentUser();
          const token = localStorage.getItem('authToken');
          
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: { user, token },
          });
        } else {
          // No valid authentication found
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch (error) {
        // Reason: only a real auth rejection (401/403) means the token is
        // invalid. A network failure carries no HTTP status, so clearing the
        // token there would force a needless re-login on an unstable connection.
        if (error?.status === 401 || error?.status === 403) {
          console.error('Auth initialization: token rejected', error);
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          dispatch({ type: 'SET_LOADING', payload: false });
        } else {
          // Couldn't reach the server — keep the cached session optimistically.
          // A later request that genuinely 401s will trigger the axios
          // interceptor's clear-and-redirect, so a stale token self-corrects.
          console.warn('Auth initialization: server unreachable, keeping cached session', error);
          const cachedUser = getStoredUser();
          const token = localStorage.getItem('authToken');
          if (cachedUser && token) {
            dispatch({ type: 'AUTH_SUCCESS', payload: { user: cachedUser, token } });
          } else {
            dispatch({ type: 'SET_LOADING', payload: false });
          }
        }
      }
    };

    initializeAuth();
  }, []);

  /**
   * Login function
   */
  const login = useCallback(async (credentials) => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      const loginResponse = await loginUser(credentials);
      const user = await getCurrentUser();
      
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user,
          token: loginResponse.access_token,
        },
      });
      
      return { success: true };
    } catch (error) {
      dispatch({
        type: 'AUTH_FAILURE',
        payload: error.message,
      });
      
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Register function
   */
  const register = useCallback(async (userData) => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      await registerUser(userData);
      
      // After successful registration, log the user in
      const loginResult = await login({
        email: userData.email,
        password: userData.password,
      });
      
      return loginResult;
    } catch (error) {
      dispatch({
        type: 'AUTH_FAILURE',
        payload: error.message,
      });
      
      return { success: false, error: error.message };
    }
  }, [login]);

  /**
   * Logout function
   */
  const logout = useCallback(async () => {
    try {
      await logoutUser();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Reason: single-user-per-device assumption — cache + mutation_log persist
      // across logout/re-login so forced re-logins (e.g., token expiry on flaky
      // network) don't lose queued offline writes or cached reads. See
      // docs/architecture/offline-pwa.md §10 #5 and offline-pwa-phase-1.md §3.7.
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  }, []);

  /**
   * Clear error function
   */
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  /**
   * Refresh user data
   */
  const refreshUser = useCallback(async () => {
    try {
      const user = await getCurrentUser();
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user,
          token: state.token,
        },
      });
      return user;
    } catch (error) {
      console.error('Error refreshing user:', error);
      throw error;
    }
  }, [state.token]);

  const value = {
    // State
    user: state.user,
    token: state.token,
    isLoading: state.isLoading,
    isAuthenticated: state.isAuthenticated,
    error: state.error,
    
    // Actions
    login,
    register,
    logout,
    clearError,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * useAuth hook for consuming auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

/**
 * Higher-order component for protecting routes
 */
export const withAuth = (Component) => {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, isLoading } = useAuth();
    
    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="spinner w-8 h-8"></div>
        </div>
      );
    }
    
    if (!isAuthenticated) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-heading mb-4">
              Authentication Required
            </h2>
            <p className="text-secondary-text mb-4">
              Please log in to access this page.
            </p>
            <a
              href="/login"
              className="btn-primary"
            >
              Go to Login
            </a>
          </div>
        </div>
      );
    }
    
    return <Component {...props} />;
  };
};